import {
	IColumnHeaderParams,
	IPageRange,
	PageTurnOptions,
	PagingHelper,
	PagingOptions,
	SearchBox,
	SearchHelper,
	ToolMenu,
	ToolMenuContent,
} from '@aon/aon-tws-common-ui';
import { ModalPopup } from '@aon/aon-tws-common-ui/build/lib/components/Modal/Modal';
import * as React from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import contentConstants from '../../contentConstants';
import { IBenchmark, IBenchmarkSelectable, IColumn } from '../../models';
import { ColumnHelper } from '../../utilities';
import { NoSearchResultsMessage } from '../common';
import { BenchmarkList } from './BenchmarkList';

type sortDirection = 'desc' | 'asc';

export interface IBenchmarkModalProps {
	isOpen: boolean;
	benchmarks: IBenchmark[];
	globalTopQuartileId?: number;
	selectedBenchmarkIds: number[];
	addBenchmark: (id: number) => void;
	removeBenchmark: (id: number) => void;
	toggleModal: () => void;
}
export interface IBenchmarkModalSate {
	benchmarks: IBenchmarkSelectable[];
	columns: IColumn[];
	searchText: string;
	sortParams: IColumnHeaderParams;
	pageRange: IPageRange;
}

const Content = styled.div`
	width: 100%;
`;
export const benchmarkColumns: IColumn[] = [
	{ name: 'selected', displayText: 'Add Benchmark', displayOrder: 1, disabled: false, checked: true },
	{ name: 'displayText', displayText: 'Name', displayOrder: 2, disabled: false, checked: true },
];

const sortableColumns: string[] = ['selected', 'displayText'];
export const defaultSort: IColumnHeaderParams = {
	sortAsc: true,
	sortedBy: 'displayText',
	columns: ColumnHelper.GetUpdateColumnHeaders(benchmarkColumns, sortableColumns, true),
};

export class BenchmarkModal extends React.Component<IBenchmarkModalProps, IBenchmarkModalSate> {
	constructor(props: IBenchmarkModalProps) {
		super(props);

		this.state = {
			benchmarks: [],
			columns: [...benchmarkColumns],
			searchText: '',
			sortParams: { ...defaultSort },
			pageRange: {
				offset: 0,
				limit: 25,
				total: this.props.benchmarks.length,
			},
		};

		this.getSelectableBenchmarks = this.getSelectableBenchmarks.bind(this);
		this.updatePaging = this.updatePaging.bind(this);
		this.updateSearchText = this.updateSearchText.bind(this);
		this.setSelectableBenchmarks = this.setSelectableBenchmarks.bind(this);
		this.sortOnColumn = this.sortOnColumn.bind(this);
		this.toggleBenchmarkSelection = this.toggleBenchmarkSelection.bind(this);
	}

	public UNSAFE_componentWillReceiveProps(props: IBenchmarkModalProps) {
		let { sortParams, pageRange, searchText } = this.state;
		if (props.isOpen !== this.props.isOpen && props.isOpen === true) {
			sortParams = { ...defaultSort };
			pageRange = {
				offset: 0,
				limit: 25,
				total: props.benchmarks.length,
			};
			searchText = '';
		}
		this.setSelectableBenchmarks(props.benchmarks, props.selectedBenchmarkIds, pageRange, sortParams, searchText);
	}

	public render() {
		const { globalTopQuartileId, isOpen, toggleModal } = this.props;
		const { benchmarks: pagedBenchmarks, columns, pageRange, searchText, sortParams } = this.state;

		return (
			<ModalPopup
				isOpen={isOpen}
				title={contentConstants.BENCHMARKS_MODAL_TITLE}
				onClosed={toggleModal}
				onSubmit={toggleModal}
				primaryButtonText={contentConstants.BUTTONS_FINISH_CLOSE}
				contentfullwidth={true}
			>
				<Content>
					<PagingOptions pageRange={pageRange} updatePageRange={this.updatePaging} />
					<ToolMenu openByDefault={true}>
						<ToolMenuContent type={'right'}>
							<SearchBox
								callbackFunc={this.updateSearchText}
								resultsCount={pagedBenchmarks.length}
								initialSearchText={searchText}
								inputSize={'sm'}
							/>
						</ToolMenuContent>
					</ToolMenu>
					<BenchmarkList
						searchText={searchText}
						selectable={true}
						benchmarks={pagedBenchmarks}
						columns={columns}
						globalTopQuartileId={globalTopQuartileId}
						sortParams={sortParams}
						handleSort={this.sortOnColumn}
						toggle={this.toggleBenchmarkSelection}
					/>
					{!pagedBenchmarks.length && searchText && <NoSearchResultsMessage />}
					<PageTurnOptions pageRange={pageRange} updatePageRange={this.updatePaging} />
				</Content>
			</ModalPopup>
		);
	}

	public toggleBenchmarkSelection(benchmarkId: number) {
		const { addBenchmark, removeBenchmark, selectedBenchmarkIds } = this.props;

		selectedBenchmarkIds.indexOf(benchmarkId) > -1 ? removeBenchmark(benchmarkId) : addBenchmark(benchmarkId);
	}

	public setSelectableBenchmarks(
		benchmarks: IBenchmark[],
		selectedBenchmarkIds: number[],
		pageRange: IPageRange,
		sortParams: IColumnHeaderParams,
		searchText: string,
	) {
		const selectableBenchmarks = this.getSelectableBenchmarks(benchmarks, selectedBenchmarkIds);

		// 1. FILTER
		const filteredBenchmarks = new SearchHelper<IBenchmarkSelectable>()
			.addColumnSelector((b: IBenchmarkSelectable) => b.displayText)
			.addColumnSelector((b: IBenchmarkSelectable) => b.description)
			.search(selectableBenchmarks, searchText);

		// 2. SORT
		let sortRules: string[] = ['displayText'];
		let sortAsc: sortDirection[] = sortParams.sortAsc ? ['asc'] : ['desc'];
		if (sortParams.sortedBy === 'selected') {
			// NOTE: lodash sorts a boolean by its stringified value
			sortRules = ['selected', 'displayText'];
			sortAsc = sortParams.sortAsc ? ['desc', 'asc'] : ['asc', 'desc'];
		}
		const sortedBenchmarks = _.orderBy(filteredBenchmarks, sortRules, sortAsc);

		// 3. PAGE
		const pagedBenchmarks = new PagingHelper<IBenchmarkSelectable>().page(sortedBenchmarks, pageRange);

		this.setState({
			benchmarks: pagedBenchmarks,
			pageRange: { ...pageRange, total: sortedBenchmarks.length },
			sortParams,
			searchText,
		});
	}

	public getSelectableBenchmarks(benchmarks: IBenchmark[], selectedBenchmarkIds: number[]) {
		const mappedBenchmarks: IBenchmarkSelectable[] = _.map(benchmarks, (benchmark: IBenchmark) => ({
			...benchmark,
			selected: selectedBenchmarkIds.indexOf(benchmark.benchmarkId) > -1,
		}));
		return mappedBenchmarks;
	}

	public updatePaging(pageRange: IPageRange) {
		this.setSelectableBenchmarks(
			this.props.benchmarks,
			this.props.selectedBenchmarkIds,
			pageRange,
			this.state.sortParams,
			this.state.searchText,
		);
	}

	public updateSearchText(searchText: string) {
		const paging: IPageRange = {
			...this.state.pageRange,
			offset: 0,
		};
		this.setSelectableBenchmarks(this.props.benchmarks, this.props.selectedBenchmarkIds, paging, this.state.sortParams, searchText);
	}

	public sortOnColumn(sortedBy: string, sortAscByDefault: boolean) {
		let sortAsc = !this.state.sortParams.sortAsc;
		if (this.state.sortParams.sortedBy !== sortedBy) {
			sortAsc = sortAscByDefault;
		}
		const sortParams = { ...this.state.sortParams, sortedBy, sortAsc };
		this.setSelectableBenchmarks(
			this.props.benchmarks,
			this.props.selectedBenchmarkIds,
			this.state.pageRange,
			sortParams,
			this.state.searchText,
		);
	}
}
